import { useEffect, useState } from 'react';
import type { OutlinePage } from '@shared/types';
import { cn } from '@/lib/utils';
import { t, n } from '@/lib/i18n';

/**
 * Contents for a long piece.
 *
 * Shows the *whole* document, not only the page on screen: a reader eight pages
 * into an उपन्यास needs to see where they are in the piece, and a list that
 * resets at every page boundary cannot tell them. Sections on other pages
 * navigate there and then scroll to the anchor.
 *
 * Appears only when it earns its place — a गजल has no sections and a rail beside
 * it is noise.
 */
export function ReadingRail({
  outline,
  page,
  pageCount,
  onGo,
  className,
}: {
  outline: OutlinePage[];
  page: number;
  pageCount: number;
  /** Navigates to a page, optionally scrolling to a heading once it loads. */
  onGo: (page: number, headingId?: string) => void;
  className?: string;
}) {
  const [activeId, setActiveId] = useState<string | null>(null);
  const current = outline.find((entry) => entry.page === page);

  /*
   * Tracks the section the reader is in.
   *
   * The top quarter of the viewport is the reading line: a heading becomes
   * current once it passes that, not when it merely enters the screen, which
   * would flip the highlight back and forth on every scroll.
   */
  useEffect(() => {
    const ids = current?.headings.map((heading) => heading.id).filter(Boolean) ?? [];
    if (!ids.length) return;

    const track = () => {
      const line = window.innerHeight * 0.25;
      let seen: string | null = null;
      for (const id of ids) {
        const node = document.getElementById(id);
        if (node && node.getBoundingClientRect().top <= line) seen = id;
      }
      setActiveId(seen ?? ids[0]);
    };

    track();
    window.addEventListener('scroll', track, { passive: true });
    return () => window.removeEventListener('scroll', track);
  }, [current]);

  const hasSections = outline.some((entry) => entry.headings.length > 0);
  if (!hasSections && pageCount <= 1) return null;

  return (
    <nav className={cn('text-sm', className)} aria-label={t('सामग्री')}>
      <h2 className="kicker np mb-2">{t('सामग्री')}</h2>

      <ol className="space-y-2">
        {outline.map((entry) => {
          const isCurrent = entry.page === page;
          return (
            <li key={entry.page}>
              {pageCount > 1 && (
                <button
                  type="button"
                  onClick={() => onGo(entry.page)}
                  aria-current={isCurrent ? 'page' : undefined}
                  className={cn(
                    'flex w-full items-baseline gap-2 rounded px-1.5 py-1 text-left transition-colors hover:bg-accent',
                    isCurrent && 'bg-accent/60',
                  )}
                >
                  <span
                    className={cn(
                      'tnum shrink-0 rounded px-1.5 text-xs',
                      isCurrent ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground',
                    )}
                  >
                    {n(entry.page)}
                  </span>
                  <span
                    className={cn(
                      'np min-w-0 flex-1 truncate',
                      isCurrent ? 'font-medium text-foreground' : 'text-muted-foreground',
                    )}
                  >
                    {entry.label || t('पृष्ठ')}
                  </span>
                </button>
              )}

              {entry.headings.length > 0 && (
                <ol className={cn('space-y-0.5 border-l', pageCount > 1 ? 'ml-3 mt-1' : '')}>
                  {entry.headings.map((heading) => (
                    <li key={`${entry.page}-${heading.id || heading.text}`}>
                      <button
                        type="button"
                        onClick={() => onGo(entry.page, heading.id)}
                        className={cn(
                          'np -ml-px block w-full border-l-2 py-1 text-left leading-snug transition-colors',
                          heading.level === 3 ? 'pl-6 text-[0.82rem]' : 'pl-3',
                          isCurrent && heading.id === activeId
                            ? 'border-primary font-medium text-foreground'
                            : 'border-transparent text-muted-foreground hover:text-foreground',
                        )}
                      >
                        {heading.text}
                      </button>
                    </li>
                  ))}
                </ol>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
